import { ContractContent, PaymentCadence, Product } from '../../types';

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const MONTHS_PER_PERIOD: Partial<Record<PaymentCadence, number>> = {
  WEEKLY: 12 / 52,
  MONTHLY: 1,
  QUARTERLY: 3,
  SEMI_ANNUAL: 6,
  ANNUAL: 12,
};

const INSTALLMENT_PAYMENTS: Partial<Record<PaymentCadence, number>> = {
  ONE_TIME: 1,
  ANNUAL_2_INSTALLMENT: 2,
  ANNUAL_3_INSTALLMENT: 3,
  ANNUAL_4_INSTALLMENT: 4,
  ONE_TIME_2_INSTALLMENT: 2,
  ONE_TIME_3_INSTALLMENT: 3,
  ONE_TIME_4_INSTALLMENT: 4,
};

export function toDate(value: string | number): Date | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d+$/.test(value)) {
    const d = new Date(Number(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (ymd) {
    return new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: string | number): string {
  const date = toDate(value);
  if (!date) return String(value);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Compact date for dense table cells, e.g. "Jul 14, 2026". */
export function formatDateShort(value: string | number): string {
  const date = toDate(value);
  if (!date) return String(value);
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

export function formatDateTime(value: string | number): string {
  const date = toDate(value);
  if (!date) return String(value);
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

/** Normalize a stored date (YYYY-MM-DD or epoch-ms string) to the YYYY-MM-DD a mutation input expects. */
export function toDateInput(value: string | number): string {
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const date = toDate(value);
  if (!date) return str;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface LineComputation {
  months: number;
  numPayments: number;
  grossSubtotal: number;
  discount: number;
  netSubtotal: number;
  discountLabel: string;
}

// Inclusive day count: a single-day window is 1 day, a Mon→Sun window is 7 days.
function daysBetween(start: string | number, end: string | number): number {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return 0;
  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  if (endMs < startMs) return 0;
  return (endMs - startMs) / (1000 * 60 * 60 * 24) + 1;
}

function monthsBetween(start: string | number, end: string | number): number {
  const days = daysBetween(start, end);
  if (days === 0) return 0;
  return Math.max(1, Math.round(days / 30.4375));
}

export function computeLine(content: ContractContent, product: Product): LineComputation {
  const months = monthsBetween(content.startDate, content.endDate);
  const installmentPayments = INSTALLMENT_PAYMENTS[product.cadence];
  const monthsPerPeriod = MONTHS_PER_PERIOD[product.cadence] ?? 1;
  // Weekly billing charges on fixed 7-day boundaries — a 9-day window (e.g. Aug 14→22)
  // covers 2 weekly charges (Aug 14 and Aug 21), not months × (52/12). Compute payments
  // directly from days so the short-window case rounds correctly; other cadences keep
  // the months-based derivation, which is close enough at monthly-or-longer granularity.
  const numPayments =
    installmentPayments ??
    (product.cadence === 'WEEKLY'
      ? Math.max(1, Math.ceil(daysBetween(content.startDate, content.endDate) / 7))
      : months / monthsPerPeriod);
  // quantity is stored ≥ 1 on the server; be defensive against a missing/0 value in cached data.
  const quantity = content.quantity && content.quantity > 0 ? content.quantity : 1;
  const grossSubtotal = product.pricePerPayment * quantity * numPayments;

  const discountedPayments = Math.min(content.discountDuration || 0, Math.ceil(numPayments));

  let discount = 0;
  let discountLabel = '—';

  switch (content.discountType) {
    case 'PERCENT_OFF':
      // Percent discount scales with quantity because it comes off the per-period gross,
      // which is itself quantity-scaled.
      discount =
        product.pricePerPayment * quantity * (content.discountAmount / 100) * discountedPayments;
      discountLabel = `${content.discountAmount}% off${
        discountedPayments ? ` × ${discountedPayments}` : ''
      }`;
      break;
    case 'AMOUNT_OFF':
      // Amount-off is a flat per-period figure — it does not scale with quantity, matching
      // the backend's "flat dollar amount off the line subtotal" semantics.
      discount = content.discountAmount * discountedPayments;
      discountLabel = `${currencyFormatter.format(content.discountAmount)} off${
        discountedPayments ? ` × ${discountedPayments}` : ''
      }`;
      break;
    default:
      break;
  }

  // A discount can never exceed the line's own value — cap it so the net can't go negative.
  const cappedDiscount = Math.min(discount, grossSubtotal);

  return {
    months,
    numPayments,
    grossSubtotal,
    discount: cappedDiscount,
    netSubtotal: grossSubtotal - cappedDiscount,
    discountLabel,
  };
}
